import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/utils/api';
import SEO from '@/components/SEO';
import StructuredData, { schemas } from '@/components/SEO/StructuredData';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [subscribeEmail, setSubscribeEmail] = useState('');

  const categories = [
    { value: '', label: 'All Posts' },
    { value: 'market_insights', label: 'Market Insights' },
    { value: 'luxury_living', label: 'Luxury Living' },
    { value: 'first_time_buyers', label: 'First-Time Buyers' },
    { value: 'agent_education', label: 'Agent Education' },
    { value: 'community', label: 'Community Highlights' }
  ];

  useEffect(() => {
    loadPosts();
  }, [selectedCategory]);

  const loadPosts = async () => {
    try {
      const url = selectedCategory 
        ? `/blog?category=${selectedCategory}` 
        : '/blog';
      const response = await api.get(url);
      setPosts(response.data);
    } catch (error) {
      console.error('Error loading posts:', error);
    }
  };

  return (
    <div className="min-h-screen" data-testid="blog-page">
      <SEO
        title="Real Estate Blog | Memphis, TN & North Mississippi Market Insights"
        description="Expert real estate advice, market insights, first-time buyer guides, and local community highlights from Bespoke Realty — serving Memphis, Southaven, Olive Branch, and across TN · MS · AR."
      />
      <StructuredData schema={schemas.breadcrumb([{ name: 'Home', url: '/' }, { name: 'Blog', url: '/blog' }])} />
      {/* Hero Banner - Image Only */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_agent-platform-28/artifacts/cve14esl_The.png)',
          }}
        ></div>
      </section>

      {/* Categories */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <Button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                variant={selectedCategory === category.value ? 'default' : 'outline'}
                className={selectedCategory === category.value ? 'bg-[#BCA480] hover:bg-[#a89470]' : ''}
                data-testid={`category-${category.value || 'all'}`}
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="py-12 bg-gradient-to-r from-[#BCA480] to-[#a89470]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Stay Connected to Bespoke Insights</h2>
          <p className="text-xl text-white/90 mb-8">
            Get the latest market updates, luxury living tips, and community highlights delivered to your inbox
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto">
            <input
              type="email"
              value={subscribeEmail}
              onChange={(e) => setSubscribeEmail(e.target.value)}
              placeholder="Enter your email address"
              className="flex-1 px-6 py-4 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
            />
            <Button
              onClick={async () => {
                if (!subscribeEmail || !subscribeEmail.includes('@')) {
                  toast.error('Please enter a valid email address');
                  return;
                }
                try {
                  await api.post('/newsletter-subscribe', { email: subscribeEmail });
                  toast.success('✅ Subscribed! Check your inbox for confirmation.');
                  setSubscribeEmail('');
                } catch (error) {
                  toast.error('Subscription failed. Please try again.');
                }
              }}
              size="lg"
              className="bg-[#434345] hover:bg-[#2d2d2f] text-white px-8 py-4"
            >
              Subscribe
            </Button>
          </div>
          <p className="text-sm text-white/80 mt-4">
            No spam, ever. Unsubscribe anytime. We value your privacy.
          </p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {posts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-posts-grid">
              {posts.map((post) => (
                <Link 
                  key={post.id} 
                  to={`/blog/${post.id}`}
                  className="group"
                  data-testid={`blog-post-${post.id}`}
                >
                  <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow h-full flex flex-col">
                    <div className="h-48 bg-gray-200 overflow-hidden">
                      <img 
                        src={post.image_url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=500&q=80'} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        <Calendar className="h-4 w-4 mr-2" />
                        {new Date(post.created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </div>
                      <h3 className="text-xl font-bold mb-3 group-hover:text-[#BCA480] transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 mb-4 flex-1">{post.excerpt}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs bg-[#BCA480]/20 text-[#BCA480] px-3 py-1 rounded-full">
                          {post.category.replace('_', ' ')}
                        </span>
                        <span className="text-sm text-[#BCA480] font-medium group-hover:underline">
                          Read More →
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No blog posts yet. Check back soon for insights and updates!</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
